import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { Lang } from '@/api';
import { useBack, useBackHandler, useGo } from '@/back';
import { PAY_PATH } from '@/covered';
import { Glyph } from '@/components/icons';
import { useFocus } from '@/focusMode';
import { LANGS, useLang } from '@/lang';
import { cleanName } from '@/name';
import { useProfileName } from '@/profileName';
import { useT } from '@/str';
import { FIRST_NAME } from '@/tgUser';
import { useTheme } from '@/theme';

const CODES: Record<Lang, string> = { uzl: 'UZ', uzk: 'ЎЗ', ru: 'RU' };

const ICON_BTN =
  'flex size-11 flex-none items-center justify-center rounded-ctl border border-border-strong p-0 text-muted-foreground no-underline';

const LETTER = /\p{L}/u;

export function TopBar() {
  const { lang, setLang } = useLang();
  const { theme, toggle } = useTheme();
  const { canBack, back } = useBack();
  const go = useGo();
  const { name } = useProfileName();
  const { pathname } = useLocation();
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = openOn === pathname;
  const setOpen = (next: boolean) => setOpenOn(next ? pathname : null);
  const trigger = useRef<HTMLButtonElement>(null);
  const bar = useRef<HTMLElement>(null);
  const t = useT();
  const focus = useFocus();

  useEffect(() => {
    const el = bar.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const ro = new ResizeObserver(() => root.style.setProperty('--top-h', `${el.offsetHeight}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--top-h');
    };
  }, []);

  useBackHandler(open, () => {
    setOpenOn(null);
    trigger.current?.focus();
  });

  const first = cleanName(name.first || FIRST_NAME);
  const hasFirst = [...first].length > 1;
  const initial = (first || name.last).match(LETTER)?.[0]?.toUpperCase() ?? 'H';

  const backButton = (
    <button
      type="button"
      aria-label={t.back}
      onClick={back}
      className="text-foreground rounded-ctl -my-1.75 -mr-2 -ml-2.5 flex size-11 flex-none items-center justify-center active:scale-[0.92]"
    >
      <Glyph name="back" size={20} weight={2.4} />
    </button>
  );

  return (
    <header
      ref={bar}
      inert={PAY_PATH.test(pathname)}
      data-focus={focus ? '' : undefined}
      className="border-border bg-tg sticky top-0 z-40 flex items-center gap-3 border-b px-[max(16px,calc((100%-600px)/2+var(--pad)))] pt-[calc(14px+var(--inset-top))] pb-2.5 data-focus:pt-[calc(6px+var(--inset-top))] data-focus:pb-1.5"
    >
      {focus ? (
        <>
          {canBack ? backButton : <div aria-hidden className="w-7.5 flex-none" />}
          <div className="flex min-w-0 flex-1 flex-col items-center px-12 text-center">
            {focus.title !== undefined && (
              <div className="text-t11 text-faint tracking-kicker w-full truncate font-bold uppercase">
                {focus.title}
              </div>
            )}
            {focus.progress !== undefined && (
              <div
                data-focus-progress
                className="text-t12 text-muted-foreground font-bold whitespace-nowrap tabular-nums"
              >
                {focus.progress}
              </div>
            )}
          </div>
          <div aria-hidden className="w-7.5 flex-none" />
        </>
      ) : (
        <>
          {canBack ? (
            backButton
          ) : (
            <div
              aria-hidden
              className="bg-primary text-primary-foreground text-t13 flex size-7.5 flex-none items-center justify-center overflow-hidden rounded-full font-extrabold tracking-[-0.02em]"
            >
              {initial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-t14 truncate leading-[1.35] font-extrabold tracking-[-0.01em]">
              {t.greet}, {hasFirst ? first : t.greetGuest}
            </div>
            <div className="text-t11 text-faint truncate tracking-wide uppercase">{t.appSub}</div>
          </div>

          <div className="relative flex-none">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={open}
              data-open={open || undefined}
              ref={trigger}
              onClick={() => setOpen(!open)}
              className="border-border-strong text-foreground text-t11h data-open:border-primary flex h-11 min-w-14 items-center gap-1.5 rounded-none border px-3 font-bold tracking-wide"
            >
              <span>{CODES[lang]}</span>
              <span className="text-t10h text-faint" aria-hidden>
                {open ? '▲' : '▼'}
              </span>
            </button>
            {open && (
              <>
                <div className="fixed inset-0 z-15" aria-hidden onClick={() => setOpen(false)} />
                <div
                  role="menu"
                  aria-label={t.srLang}
                  className="bg-card border-border-strong rounded-ctl absolute top-12.5 right-0 z-20 flex min-w-37 flex-col overflow-hidden border"
                >
                  {LANGS.map((l, i) => {
                    const on = l.value === lang;
                    return (
                      <button
                        key={l.value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={on}
                        data-on={on || undefined}
                        data-last={i === LANGS.length - 1 || undefined}
                        onClick={() => {
                          setLang(l.value);
                          setOpen(false);
                        }}
                        className="border-border text-t13 text-lang-idle data-on:bg-on-acc data-on:text-primary flex items-center justify-between gap-2.5 border-b px-3.75 py-3.25 text-left font-semibold active:transform-none data-last:border-b-0"
                      >
                        <span>{l.label}</span>
                        <span className="text-t10h tracking-wide opacity-70">{CODES[l.value]}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            className={ICON_BTN}
            onClick={toggle}
            aria-label={t.themeRow}
            title={t.themeRow}
          >
            {theme === 'dark' ? (
              <Glyph name="sun" data-icon="sun" round={false} />
            ) : (
              <Glyph name="moon" data-icon="moon" round={false} />
            )}
          </button>

          <a
            className={ICON_BTN}
            href="#/settings"
            aria-label={t.settings}
            title={t.settings}
            onClick={(e) => {
              e.preventDefault();
              go('/settings');
            }}
          >
            <Glyph name="sliders" round={false} strokeLinecap="round" />
          </a>
        </>
      )}
    </header>
  );
}
